import { useState } from 'react';
import { pageContent } from '@/lib/store';
import type { PageContent } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Save, FileEdit, ChevronDown } from 'lucide-react';

const pages = [
  { id: 'home', label: 'Homepage' },
  { id: 'about', label: 'About Us' },
  { id: 'services_dlp', label: 'Service - DLP Chip' },
  { id: 'services_power', label: 'Service - Power & Heating' },
  { id: 'services_display', label: 'Service - Display Discolouration' },
  { id: 'services_laser', label: 'Service - Laser & Lamp' },
  { id: 'repair_enquiry', label: 'Repair Enquiry' },
  { id: 'warranty', label: 'Warranty & Returns' },
  { id: 'terms', label: 'Terms & Conditions' },
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'locations', label: 'Locations' },
  { id: 'pricing', label: 'Pricing' },
];

export default function PageEditor() {
  const [selectedPage, setSelectedPage] = useState(pages[0].id);
  const [content, setContent] = useState<PageContent>(() => pageContent.get(pages[0].id));
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const loadPage = (pageId: string) => {
    setSelectedPage(pageId);
    setContent(pageContent.get(pageId));
    setDropdownOpen(false);
  };

  const handleSave = () => {
    const updated = { ...content, lastEdited: new Date().toISOString() };
    pageContent.set(selectedPage, updated);
    setContent(updated);
    toast.success(`"${pages.find(p => p.id === selectedPage)?.label}" content saved`);
  };

  const updateField = (field: keyof PageContent, value: string) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const currentPage = pages.find(p => p.id === selectedPage);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white">Page Editor</h1>
        <p className="text-white/60 text-sm mt-1">Edit content for each page of your website.</p>
      </div>

      {/* Page Selector */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center justify-between w-full sm:w-80 h-11 px-4 bg-[#3d3834] border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
        >
          <span className="flex items-center gap-2">
            <FileEdit className="w-4 h-4 text-[#FFDD00]" />
            {currentPage?.label}
          </span>
          <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        {dropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-full sm:w-80 bg-[#3d3834] border border-white/10 rounded-lg shadow-xl z-10 max-h-72 overflow-auto">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => loadPage(page.id)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  selectedPage === page.id ? 'bg-[#FFDD00]/10 text-[#0057B7]' : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {page.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Editor Form */}
      <div className="card-dark p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-white">Edit Content</h3>
          {content.lastEdited && (
            <span className="text-xs text-white/60">
              Last edited: {new Date(content.lastEdited).toLocaleString('en-GB')}
            </span>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">Page Title</Label>
          <Input
            value={content.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Enter page title"
            className="h-11 bg-[#0057B7] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">Meta Description</Label>
          <Textarea
            value={content.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Enter meta description"
            className="min-h-[80px] bg-[#0057B7] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">Keywords</Label>
          <Input
            value={content.keywords}
            onChange={(e) => updateField('keywords', e.target.value)}
            placeholder="Enter keywords, comma separated"
            className="h-11 bg-[#0057B7] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">H1 Heading</Label>
          <Input
            value={content.h1}
            onChange={(e) => updateField('h1', e.target.value)}
            placeholder="Main page heading"
            className="h-11 bg-[#0057B7] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">OG Title</Label>
          <Input
            value={content.ogTitle}
            onChange={(e) => updateField('ogTitle', e.target.value)}
            placeholder="Open Graph title"
            className="h-11 bg-[#0057B7] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">OG Description</Label>
          <Textarea
            value={content.ogDescription}
            onChange={(e) => updateField('ogDescription', e.target.value)}
            placeholder="Open Graph description"
            className="min-h-[80px] bg-[#0057B7] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80 text-sm">Body Content</Label>
          <Textarea
            value={content.bodyContent}
            onChange={(e) => updateField('bodyContent', e.target.value)}
            placeholder="Main page body content"
            className="min-h-[200px] bg-[#0057B7] border-white/10 text-white font-mono text-sm"
          />
        </div>

        <Button onClick={handleSave} className="btn-primary">
          <Save className="w-4 h-4" />
          Save Page Content
        </Button>
      </div>
    </div>
  );
}